import { useMemo, useState } from 'react'
import { Dialog } from '../components/Dialog'
import { t } from '../i18n'
import type { MarketplaceItem, MenuItem } from '../api/types'
import type { PosDiscount } from '../lightspeed/types'
import { matchMenu } from './marketplace-model'

/** Cents → "CHF 18.90". Cents are the unit for every amount on both sides of this integration. */
function formatChf(cents?: number): string {
  if (typeof cents !== 'number') return ''
  return `CHF ${(cents / 100).toFixed(2)}`
}

function PickerRow({
  selected,
  onSelect,
  title,
  trailing,
  subtitle,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  trailing?: string
  subtitle?: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex min-h-[60px] w-full items-center justify-between gap-3 rounded-lg px-[18px] py-2 text-left transition ${
        selected
          ? 'border-[1.5px] border-brand bg-brand-50'
          : 'border border-line bg-white hover:bg-canvas'
      }`}
    >
      <span className="min-w-0">
        <span
          className={`block truncate text-[18px] font-semibold ${selected ? 'text-brand' : 'text-ink'}`}
        >
          {title}
        </span>
        {subtitle && <span className="block text-[14px] text-ink-soft">{subtitle}</span>}
      </span>
      {trailing && <span className="shrink-0 text-[16px] text-ink-soft">{trailing}</span>}
    </button>
  )
}

/** "Récompense à relier" — the reward has no Lightspeed discount behind it yet (Figma 2260:369).
 *
 * A reward is only claimable if it maps onto something the POS can actually discount, so when
 * that mapping is missing the cashier fixes it here rather than being told "no" mid-service:
 * pick the item, pick the Lightspeed discount, and the claim resumes immediately.
 *
 * The two sides come from different places, and that is the point:
 *
 * - `menu` is the merchant's Lightspeed MENU (backend, TechSpec B4). Not the open order: the link
 *   is durable configuration keyed on the menu item id, so it holds for every future sale and can
 *   be made with no sale open at all. The web-extension JS API has no catalogue call, so this
 *   cannot be read from the POS at runtime.
 * - `discounts` IS live POS data (`pos_getAvailableDiscounts`) — the merchant's own back-office
 *   codes, which is why the reward's discount is not always `BADGER_FREE`: a −50% or −10% code is
 *   equally valid, generalising the 100%-only assumption pinned in `pos-api.ts`.
 */
export function RewardLinkDialog({
  item,
  menu,
  discounts,
  busy = false,
  onLink,
  onCancel,
  onNoSuitableDiscount,
}: {
  item: MarketplaceItem
  menu: MenuItem[]
  discounts: PosDiscount[]
  busy?: boolean
  onLink: (link: { posItemId: string; discountCode: string }) => void
  onCancel: () => void
  onNoSuitableDiscount: () => void
}) {
  const [query, setQuery] = useState('')
  const [itemId, setItemId] = useState<string | null>(null)
  const [discountCode, setDiscountCode] = useState<string | null>(null)

  const results = useMemo(() => matchMenu(menu, query), [menu, query])
  const chosenItem = menu.find((m) => m.item_id === itemId)
  const chosenDiscount = discounts.find((d) => d.code === discountCode)
  const ready = Boolean(chosenItem && chosenDiscount?.code)

  return (
    <Dialog title={t('rewardToLink')} titleHidden onDismiss={onCancel} width={960}>
      <header className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#fdeee9] text-[22px]"
        >
          ⚠️
        </span>
        <div>
          <p className="text-[26px] font-semibold text-ink">{t('rewardToLink')}</p>
          <p className="mt-1.5 text-[16px] leading-normal text-ink-soft">
            {t('rewardNotLinkedYet').replace('{reward}', item.label)}
            <br />
            {t('rewardLinkThenUse')}
          </p>
        </div>
      </header>

      <div className="mt-6 border-t border-line pt-[22px]">
        <div className="grid grid-cols-2 gap-12">
          <section>
            <h3 className="text-[13px] font-semibold tracking-[1.04px] text-ink-soft">
              {t('stepChooseItem')}
            </h3>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('searchItemPlaceholder')}
              aria-label={t('stepChooseItem')}
              className="mt-3.5 h-[52px] w-full rounded-lg border border-line bg-white px-4 text-[18px] font-medium text-ink outline-none placeholder:text-ink-muted focus:border-brand"
            />
            {/* Bounded + scrollable: a real menu runs to hundreds of items, and letting the list
              * size the dialog pushed the primary button off the 834px POS viewport. */}
            <div className="mt-3 flex max-h-[295px] flex-col gap-3 overflow-y-auto pr-1">
              {results.map((m) => (
                <PickerRow
                  key={m.item_id}
                  selected={m.item_id === itemId}
                  onSelect={() => setItemId(m.item_id)}
                  title={m.name}
                  trailing={formatChf(m.price_cents)}
                />
              ))}
              {results.length === 0 && (
                <p className="py-4 text-[16px] text-ink-muted">
                  {menu.length === 0 ? t('menuUnavailable') : t('noItemsFound')}
                </p>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-[13px] font-semibold tracking-[1.04px] text-ink-soft">
              {t('stepChooseDiscount')}
            </h3>
            <div className="mt-3.5 flex max-h-[295px] flex-col gap-3 overflow-y-auto pr-1">
              {discounts.map((d) => (
                <PickerRow
                  key={d.code ?? d.name}
                  selected={Boolean(d.code) && d.code === discountCode}
                  onSelect={() => d.code && setDiscountCode(d.code)}
                  title={d.code ?? d.name ?? ''}
                  subtitle={d.name}
                />
              ))}
              {discounts.length === 0 && (
                <p className="py-4 text-[16px] text-ink-muted">{t('noDiscountsConfigured')}</p>
              )}
            </div>
            <p className="mt-3 text-[13px] text-ink-soft">{t('discountsFromBackOffice')}</p>
          </section>
        </div>
      </div>

      {/* Reads back the pairing in one line before it is committed — the cashier is mapping a
       * reward onto a real discount on a live sale, so the outcome is spelled out, not implied. */}
      <p className="mt-7 border-t border-line pt-6 text-center text-[16px] font-medium text-brand">
        {ready ? (
          <>
            {chosenItem?.name} &nbsp;↔&nbsp; {chosenDiscount?.code} &nbsp;·&nbsp;{' '}
            {t('rewardAppliedToThisItem')}
          </>
        ) : (
          <span className="text-ink-muted">{t('pickItemAndDiscount')}</span>
        )}
      </p>

      <div className="mt-7 flex items-center justify-between gap-6">
        <button
          type="button"
          onClick={onNoSuitableDiscount}
          className="text-[18px] font-semibold text-ink-soft transition hover:text-ink"
        >
          {t('cannotFindDiscount')} →
        </button>
        <button
          type="button"
          disabled={!ready || busy}
          onClick={() =>
            onLink({
              posItemId: chosenItem?.item_id ?? '',
              discountCode: chosenDiscount?.code ?? '',
            })
          }
          className="h-[60px] rounded-lg bg-brand px-12 text-[19px] font-semibold text-white transition hover:bg-brand-dark disabled:opacity-40"
        >
          {t('linkAndUseReward')}
        </button>
      </div>
    </Dialog>
  )
}
